import {
  InputNumber,
  Segmented,
  Select,
  Table,
  Upload,
  type TableProps,
  type UploadProps
} from 'antd'
import clsx from 'clsx'
import { useMemo, type FC } from 'react'
import { AiOutlineInbox } from 'react-icons/ai'
import { FaDownload, FaFileCsv } from 'react-icons/fa'

import { Alert, Button } from '@/components/elements'
import {
  ACTIONS,
  SHIPMENT_ORDER_CSV_IMPORT
} from '@/constants/common/message.constants'

import { type CsvReadConfig, type ParsedCsv } from '../../models'
import {
  encodingDisplayLabel,
  SELECTABLE_ENCODINGS,
  type EncodingOverride
} from '../../utils'
import { FieldReferenceHelp } from '../FieldReferenceHelp'
import { StandardFormatHelp } from '../StandardFormatHelp'

const { Dragger } = Upload

/** プレビューに表示する最大行数（antd Table は非仮想化のため先頭のみ描画して負荷を抑える）。 */
const PREVIEW_ROW_LIMIT = 100

type Props = {
  parsed: ParsedCsv | null
  readConfig: CsvReadConfig
  onChangeReadConfig: (next: CsvReadConfig) => void
  onSelectFile: (file: File) => void
  parseError: string | null
  encodingOverride: EncodingOverride
  onChangeEncoding: (next: EncodingOverride) => void
  /**
   * 通常フォーマット（標準取込）用の簡易表示。
   * フォーマットが固定のため、開始行・開始列・文字コード等の読み取り設定を出さない
   * （ファイル選択とプレビューのみ）。
   */
  simpleMode?: boolean
  /**
   * テンプレートCSVダウンロードボタンの表示要否。
   * standard / saved（フォーマット選択済み）でtrue、adhocはfalse。
   */
  canDownloadTemplate?: boolean
  onDownloadTemplate?: () => void
}

type PreviewRow = {
  key: number
  /** 1始まりの行番号 */
  lineNo: number
  cells: string[]
}

/**
 * ステップ1: ファイル選択・開始行・開始列。
 * - CSVファイルをドラッグ&ドロップ/クリックで選択し、S3に上げずメモリでパース（papaparse）。
 * - パース結果を antd Table でプレビュー。ヘッダー行ハイライト・開始行/開始列より前はスキップ表示。
 * - 「1行目=項目名/データ」切替、ヘッダー行番号、データ開始行番号、開始列番号を指定できる。
 */
export const FileView: FC<Props> = ({
  parsed,
  readConfig,
  onChangeReadConfig,
  onSelectFile,
  parseError,
  encodingOverride,
  onChangeEncoding,
  simpleMode = false,
  canDownloadTemplate = false,
  onDownloadTemplate
}) => {
  const { hasHeader, headerRowNo, startRowNo, startColNo } = readConfig

  // beforeUpload で false を返してアップロードを止め、生の File を受け取る（S3に上げない）
  const beforeUpload: NonNullable<UploadProps['beforeUpload']> = (file) => {
    onSelectFile(file as unknown as File)
    return false
  }

  // テンプレートCSVダウンロード（standard=固定列、saved（選択済み）=保存フォーマットの列名。
  // adhocは対象外なので出さない）。列仕様ヘルプ（StandardFormatHelp/FieldReferenceHelp）と
  // 同じ行に左右端寄せで並べる。
  const templateDownloadButton = canDownloadTemplate && (
    <Button
      type="link"
      size="small"
      icon={<FaDownload />}
      onClick={onDownloadTemplate}
      data-testid="shipment-order-csv-import__download-template"
    >
      {ACTIONS.DOWNLOAD_TEMPLATE}
    </Button>
  )

  const columnCount = parsed?.columnCount ?? 0

  const columns: TableProps<PreviewRow>['columns'] = useMemo(() => {
    const lineNoColumn = {
      // 行番号の見出しラベルは不要。空ヘッダーにして列幅を詰め、中央寄せにする。
      title: '',
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 36,
      fixed: 'left' as const,
      align: 'center' as const,
      // 行番号は「行ヘッダー」。テーブルのヘッダー行と同じ見た目にする
      // （背景 = theme Table.headerBg #fbfbfb、文字色 = ヘッダーと同じ secondary）。
      // antd の fixed-left セルは独自に白背景を当て className では specificity 負けするため、
      // inline style で上書きして確実にヘッダーと同色にする。paddingInline も詰めて幅を短く。
      onCell: () => ({
        style: { backgroundColor: '#fbfbfb', paddingInline: 6 }
      }),
      render: (n: number) => (
        <span className="tabular-nums text-secondary">{n}</span>
      )
    }
    const cellColumns = Array.from({ length: columnCount }, (_, idx) => {
      // 開始列より前の列は読み取り対象外。背景を少し濃いめのグレーで示す。
      const isBeforeStartCol = idx + 1 < startColNo
      const skipCellClass = isBeforeStartCol
        ? { className: 'bg-neutral-4' }
        : {}
      return {
        title: (
          <span
            className={clsx(
              'text-3xs',
              isBeforeStartCol ? 'text-neutral-6' : 'text-secondary'
            )}
          >
            {SHIPMENT_ORDER_CSV_IMPORT.COLUMN_LABEL(idx)}
          </span>
        ),
        key: `col-${idx}`,
        width: 120,
        ellipsis: true,
        onHeaderCell: () => skipCellClass,
        onCell: () => skipCellClass,
        render: (_: unknown, row: PreviewRow) => (
          <span className="text-xs">{row.cells[idx] ?? ''}</span>
        )
      }
    })
    return [lineNoColumn, ...cellColumns]
  }, [columnCount, startColNo])

  const dataSource: PreviewRow[] = useMemo(() => {
    if (!parsed) return []
    // プレビューは先頭 PREVIEW_ROW_LIMIT 行のみ。全行は parsed.rows に保持され、
    // 取り込み（buildEffectiveTable）は全行を対象にするのでここでの制限は表示のみ。
    return parsed.rows.slice(0, PREVIEW_ROW_LIMIT).map((cells, i) => ({
      key: i,
      lineNo: i + 1,
      cells
    }))
  }, [parsed])

  // ヘッダー行 / スキップ行（開始行より前）の見た目
  const rowClassName = (row: PreviewRow): string => {
    if (hasHeader && row.lineNo === headerRowNo) {
      return 'bg-geek-blue-1 font-medium'
    }
    if (row.lineNo < startRowNo) {
      return 'bg-neutral-4 text-neutral-6'
    }
    return ''
  }

  return (
    <div className="flex flex-col gap-3">
      {simpleMode ? (
        <>
          <Alert title={SHIPMENT_ORDER_CSV_IMPORT.STANDARD_FILE_ALERT_MESSAGE}>
            <span className="text-xs">
              {SHIPMENT_ORDER_CSV_IMPORT.ONLY_CSV_FILE}
            </span>
          </Alert>
          {/* 標準フォーマットの列仕様ヘルプ（各列の変換・できることの一覧）と
              テンプレートCSVダウンロードを同じ行に左右端寄せで並べる */}
          <div className="flex items-center justify-between">
            <StandardFormatHelp />
            {templateDownloadButton}
          </div>
        </>
      ) : (
        <>
          <Alert title={SHIPMENT_ORDER_CSV_IMPORT.FILE_ALERT_MESSAGE}>
            <span className="text-xs">
              {SHIPMENT_ORDER_CSV_IMPORT.FILE_ALERT_DESCRIPTION}
            </span>
          </Alert>
          {/* 入力できる項目・反映先のリファレンス（ファイル準備段階でも見られるよう
              マッピング画面より前に出す）とテンプレートCSVダウンロード（saved選択済みのみ表示）を
              同じ行に左右端寄せで並べる */}
          <div className="flex items-center justify-between">
            <FieldReferenceHelp />
            {templateDownloadButton}
          </div>
        </>
      )}

      {/* ファイル選択（未選択時はドラッガー、選択済みはサマリ表示） */}
      {parsed ? (
        <div className="flex flex-col gap-1 rounded border border-table-border p-3">
          <div className="flex flex-wrap items-center gap-3">
            <FaFileCsv className="text-xl text-green" />
            <div className="min-w-[200px] flex-1">
              <div className="font-medium text-secondary">
                {parsed.fileName}
              </div>
              <div className="text-2xs tabular-nums text-gray-2">
                {SHIPMENT_ORDER_CSV_IMPORT.FILE_SUMMARY(
                  parsed.rowCount,
                  parsed.columnCount,
                  parsed.encoding
                )}
              </div>
            </div>
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept=".csv,text/csv"
              maxCount={1}
            >
              <Button size="small">
                {SHIPMENT_ORDER_CSV_IMPORT.CHANGE_FILE}
              </Button>
            </Upload>
          </div>
          <div className="text-2xs text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.ONLY_CSV_FILE}
          </div>
        </div>
      ) : (
        <Dragger
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept=".csv,text/csv"
          maxCount={1}
          multiple={false}
        >
          <p className="flex justify-center">
            <AiOutlineInbox className="text-3xl text-primary" />
          </p>
          <p className="text-sm text-secondary">
            {SHIPMENT_ORDER_CSV_IMPORT.CLICK_OR_DROP_CSV}
          </p>
          <p className="text-2xs text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.ONLY_CSV_FILE}
          </p>
        </Dragger>
      )}

      {parseError && (
        <Alert color="red" title={parseError}>
          <span className="text-xs">
            {SHIPMENT_ORDER_CSV_IMPORT.ONLY_CSV_FILE}
          </span>
        </Alert>
      )}

      {/* 開始行・開始列の指定（ファイル選択後のみ表示） */}
      {parsed && (
        <>
          {!simpleMode && (
            <>
              {/* 文字コード（自動判定の誤りを手動で上書き＝再デコード） */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-2 text-xs">
                  <span className="font-medium text-secondary">
                    {SHIPMENT_ORDER_CSV_IMPORT.ENCODING_FIELD_LABEL}
                  </span>
                  <Select<EncodingOverride>
                    size="small"
                    value={encodingOverride}
                    onChange={onChangeEncoding}
                    className="w-32"
                    data-testid="shipment-order-csv-import__encoding-select"
                    options={[
                      {
                        value: 'auto',
                        label: SHIPMENT_ORDER_CSV_IMPORT.ENCODING_AUTO
                      },
                      ...SELECTABLE_ENCODINGS.map((enc) => ({
                        value: enc,
                        label: encodingDisplayLabel(enc)
                      }))
                    ]}
                  />
                  <span className="text-gray-2">
                    {SHIPMENT_ORDER_CSV_IMPORT.ENCODING_HINT}
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-2 text-xs">
                  <span className="font-medium text-secondary">
                    {SHIPMENT_ORDER_CSV_IMPORT.FIRST_ROW}
                  </span>
                  <Segmented
                    size="small"
                    value={hasHeader ? 'header' : 'none'}
                    onChange={(v) =>
                      onChangeReadConfig({
                        ...readConfig,
                        hasHeader: v === 'header'
                      })
                    }
                    data-testid="shipment-order-csv-import__has-header-segmented"
                    options={[
                      {
                        label: SHIPMENT_ORDER_CSV_IMPORT.FIRST_ROW_HEADER,
                        value: 'header'
                      },
                      {
                        label: SHIPMENT_ORDER_CSV_IMPORT.FIRST_ROW_DATA,
                        value: 'none'
                      }
                    ]}
                  />
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <span className="font-medium text-secondary">
                    {SHIPMENT_ORDER_CSV_IMPORT.HEADER_ROW}
                  </span>
                  <InputNumber
                    size="small"
                    min={1}
                    max={Math.max(1, parsed.rowCount)}
                    value={hasHeader ? headerRowNo : null}
                    disabled={!hasHeader}
                    onChange={(v) =>
                      onChangeReadConfig({ ...readConfig, headerRowNo: v || 1 })
                    }
                    className="w-16"
                    data-testid="shipment-order-csv-import__header-row-input"
                  />
                  <span className="text-gray-2">
                    {SHIPMENT_ORDER_CSV_IMPORT.ROW_SUFFIX}
                  </span>
                </span>

                <span className="inline-flex items-center gap-1.5 text-xs">
                  <span className="font-medium text-secondary">
                    {SHIPMENT_ORDER_CSV_IMPORT.DATA_START_ROW}
                  </span>
                  <InputNumber
                    size="small"
                    min={1}
                    max={Math.max(1, parsed.rowCount)}
                    value={startRowNo}
                    onChange={(v) =>
                      onChangeReadConfig({ ...readConfig, startRowNo: v || 1 })
                    }
                    className="w-16"
                    data-testid="shipment-order-csv-import__start-row-input"
                  />
                  <span className="text-gray-2">
                    {SHIPMENT_ORDER_CSV_IMPORT.ROW_FROM_SUFFIX}
                  </span>
                </span>

                {/* 開始列指定 */}
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <span className="font-medium text-secondary">
                    {SHIPMENT_ORDER_CSV_IMPORT.START_COLUMN}
                  </span>
                  <InputNumber
                    size="small"
                    min={1}
                    max={Math.max(1, parsed.columnCount)}
                    value={startColNo}
                    onChange={(v) =>
                      onChangeReadConfig({ ...readConfig, startColNo: v || 1 })
                    }
                    className="w-16"
                    data-testid="shipment-order-csv-import__start-col-input"
                  />
                  <span className="text-gray-2">
                    {SHIPMENT_ORDER_CSV_IMPORT.COLUMN_FROM_SUFFIX}
                  </span>
                </span>
              </div>
            </>
          )}

          {/* プレビュー */}
          <div className="text-xs text-secondary">
            {SHIPMENT_ORDER_CSV_IMPORT.FILE_PREVIEW}
            <span className="ml-3.5">
              <span className="mr-1 inline-block h-2.5 w-2.5 rounded-[2px] bg-geek-blue-1 align-[-1px]" />
              {SHIPMENT_ORDER_CSV_IMPORT.LEGEND_HEADER_ROW}
            </span>
            <span className="ml-3">
              <span className="mr-1 inline-block h-2.5 w-2.5 rounded-[2px] border border-neutral-6 bg-neutral-4 align-[-1px]" />
              {SHIPMENT_ORDER_CSV_IMPORT.LEGEND_SKIP_BEFORE_START}
            </span>
            {parsed.rowCount > PREVIEW_ROW_LIMIT && (
              <span className="ml-3 text-gray-2">
                {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_TRUNCATED(
                  PREVIEW_ROW_LIMIT,
                  parsed.rowCount
                )}
              </span>
            )}
          </div>
          <Table<PreviewRow>
            size="small"
            bordered
            rowKey="key"
            pagination={false}
            columns={columns}
            dataSource={dataSource}
            rowClassName={rowClassName}
            scroll={{ x: 'max-content', y: 240 }}
          />
        </>
      )}
    </div>
  )
}
